/**
 * Contribution grids — the GitHub-style heatmap of who changed what, when.
 *
 * This is a second projection of the same change log that produces the file
 * tree. Because both are folds over one immutable list, the heatmap can never
 * drift from the files it describes.
 *
 * DATA DEFINITIONS
 *
 *   A DayKey is a String "YYYY-MM-DD".
 *   Interpretation: one calendar day IN THE TEAM'S OWN TIMEZONE. This matters:
 *   the team is in India (UTC+05:30), and a commit made at 23:30 IST is 18:00
 *   UTC the same day, but one made at 00:30 IST is 19:00 UTC the PREVIOUS day.
 *   Bucketing by UTC would smear a normal evening's work across two squares.
 *   The zone is a parameter so the function stays testable and honest.
 *
 *   A ContributionDay is { date, total, added, modified, deleted, renamed }.
 *   Interpretation: one square in the grid. We keep the breakdown by kind, not
 *   just a total, so the tooltip can say "12 added, 3 modified".
 *
 *   A ContributionGrid is { days, weeks, maxTotal, totalChanges, from, to }.
 *   Interpretation: a full year (or any window) laid out as GitHub does it —
 *   columns are weeks, rows are weekdays, Sunday at the top.
 */
import {} from './change.js';
import {} from './user.js';
/** The team's timezone offset in minutes. India Standard Time is UTC+05:30. */
export const IST_OFFSET_MINUTES = 330;
/**
 * The calendar day an instant falls on, in a given timezone offset.
 * dayKeyOf : String Number -> DayKey
 */
export function dayKeyOf(iso, offsetMinutes = IST_OFFSET_MINUTES) {
    const shifted = new Date(Date.parse(iso) + offsetMinutes * 60_000);
    return shifted.toISOString().slice(0, 10);
}
/** The DayKey `n` days after `day`. addDays : DayKey Number -> DayKey */
export function addDays(day, n) {
    const t = Date.parse(`${day}T00:00:00Z`) + n * 86_400_000;
    return new Date(t).toISOString().slice(0, 10);
}
/** Day of week for a DayKey: 0 = Sunday .. 6 = Saturday. weekdayOf : DayKey -> Number */
export const weekdayOf = (day) => new Date(`${day}T00:00:00Z`).getUTCDay();
/** Whole days between two DayKeys. daysBetween : DayKey DayKey -> Number */
export const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
/** An empty square. emptyDay : DayKey -> ContributionDay */
const emptyDay = (date) => ({ date, total: 0, added: 0, modified: 0, deleted: 0, renamed: 0 });
/**
 * Tally changes into per-day counts.
 * tallyByDay : [Change] Number -> Map<DayKey, ContributionDay>
 */
export function tallyByDay(changes, offsetMinutes = IST_OFFSET_MINUTES) {
    const byDay = new Map();
    for (const c of changes) {
        const date = dayKeyOf(c.at, offsetMinutes);
        const prev = byDay.get(date) ?? emptyDay(date);
        byDay.set(date, {
            ...prev,
            total: prev.total + 1,
            added: prev.added + (c.kind === 'added' ? 1 : 0),
            modified: prev.modified + (c.kind === 'modified' ? 1 : 0),
            deleted: prev.deleted + (c.kind === 'deleted' ? 1 : 0),
            renamed: prev.renamed + (c.kind === 'renamed' ? 1 : 0),
        });
    }
    return byDay;
}
/**
 * Lay a window of days out as GitHub does: whole weeks, Sunday-first, with the
 * first and last weeks padded so every column has exactly seven rows.
 *
 * buildGrid : [Change] DayKey DayKey Number -> ContributionGrid
 */
export function buildGrid(changes, from, to, offsetMinutes = IST_OFFSET_MINUTES) {
    const byDay = tallyByDay(changes, offsetMinutes);
    // Pad outwards to week boundaries: back to Sunday, forward to Saturday.
    const start = addDays(from, -weekdayOf(from));
    const end = addDays(to, 6 - weekdayOf(to));
    const days = [];
    for (let i = 0; i <= daysBetween(start, end); i++) {
        const date = addDays(start, i);
        days.push(byDay.get(date) ?? emptyDay(date));
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7)
        weeks.push(days.slice(i, i + 7));
    return {
        days,
        weeks,
        maxTotal: days.reduce((m, d) => (d.total > m ? d.total : m), 0),
        totalChanges: days.reduce((s, d) => s + d.total, 0),
        from: start,
        to: end,
    };
}
/**
 * Build the grid for the twelve months ending on `today`.
 * Convenience wrapper so callers do not repeat the window arithmetic.
 * buildYearGrid : [Change] DayKey Number -> ContributionGrid
 */
export const buildYearGrid = (changes, today, offsetMinutes = IST_OFFSET_MINUTES) => buildGrid(changes, addDays(today, -364), today, offsetMinutes);
/**
 * Bucket a day's activity into one of five intensity levels, 0-4, the way
 * GitHub shades its squares. Level is RELATIVE to the busiest day in the grid,
 * so a quiet month still shows contrast.
 * intensityOf : ContributionDay Number -> Number
 */
export function intensityOf(day, maxTotal) {
    if (day.total === 0 || maxTotal === 0)
        return 0;
    const ratio = day.total / maxTotal;
    if (ratio <= 0.25)
        return 1;
    if (ratio <= 0.5)
        return 2;
    if (ratio <= 0.75)
        return 3;
    return 4;
}
/**
 * Summarise each person's contribution.
 * statsByAuthor : [Change] Number -> [AuthorStats]  (busiest first)
 */
export function statsByAuthor(changes, offsetMinutes = IST_OFFSET_MINUTES) {
    const grouped = new Map();
    for (const c of changes) {
        const list = grouped.get(c.authorId);
        if (list)
            list.push(c);
        else
            grouped.set(c.authorId, [c]);
    }
    const out = [];
    for (const [authorId, cs] of grouped) {
        const times = cs.map(c => Date.parse(c.at)).sort((a, b) => a - b);
        const days = [...new Set(cs.map(c => dayKeyOf(c.at, offsetMinutes)))].sort();
        out.push({
            authorId,
            total: cs.length,
            added: cs.filter(c => c.kind === 'added').length,
            modified: cs.filter(c => c.kind === 'modified').length,
            deleted: cs.filter(c => c.kind === 'deleted').length,
            renamed: cs.filter(c => c.kind === 'renamed').length,
            firstAt: new Date(times[0]).toISOString(),
            lastAt: new Date(times[times.length - 1]).toISOString(),
            activeDays: days.length,
            longestStreak: longestStreakOf(days),
        });
    }
    return out.sort((a, b) => b.total - a.total);
}
/**
 * The longest run of consecutive calendar days in a SORTED, de-duplicated list.
 * longestStreakOf : [DayKey] -> Number
 */
export function longestStreakOf(sortedDays) {
    if (sortedDays.length === 0)
        return 0;
    let best = 1;
    let run = 1;
    for (let i = 1; i < sortedDays.length; i++) {
        run = daysBetween(sortedDays[i - 1], sortedDays[i]) === 1 ? run + 1 : 1;
        if (run > best)
            best = run;
    }
    return best;
}
//# sourceMappingURL=contributions.js.map